import { XCircleIcon } from "@heroicons/react/24/solid";
import React, { useMemo, useRef, useState } from "react";

import { DarkThemeSingleSelect } from "../../../../components";
import { useAppSelector } from "../../../../redux/hooks";
import { selectPlayers } from "../../../../redux/slices/playerSlice";
import { getFreeformSelectStyles } from "../shared/rebootLiveSelectStyles";

interface PlayerSelectStepProps {
  selectedPlayer: string;
  onPlayerChange: (playerId: string) => void;
  error?: string | null;
  allowRetry?: boolean;
  onRetry?: () => void;
  isDisabled?: boolean;
}

export const PlayerSelectStep: React.FC<PlayerSelectStepProps> = ({
  selectedPlayer,
  onPlayerChange,
  error,
  allowRetry = true,
  onRetry,
  isDisabled = false,
}) => {
  const [playerSearchInput, setPlayerSearchInput] = useState("");
  const players = useAppSelector(selectPlayers);
  const containerRef = useRef<HTMLDivElement>(null);

  const playerOptions = players.playerList.map((player) => ({
    value: player.orgPlayerId,
    label: `${player.firstName} ${player.lastName}`,
  }));

  const rebootPlayerSelectStyles = useMemo(
    () => getFreeformSelectStyles({ includeOptionOverrides: true }),
    [],
  );

  return (
    <div className="box-border content-stretch flex flex-col gap-[24px] items-center pb-0 pt-[12px] px-0 relative shrink-0 w-full max-w-[960px]">
      {/* Error State */}
      {error && (
        <div className="bg-[#1d1d1f] h-[44px] relative rounded-[8px] shrink-0 w-[600px]">
          <div
            aria-hidden="true"
            className="absolute border-[0.833px] border-[rgba(255,255,255,0.1)] border-solid inset-0 pointer-events-none rounded-[8px]"
          />
          <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex flex-col h-[44px] items-start justify-center px-[16px] py-0 relative w-full">
            <div className="basis-0 content-stretch flex gap-[12px] grow items-center min-h-px min-w-px relative shrink-0 w-full">
              <div className="relative shrink-0 size-[20px]">
                <XCircleIcon className="size-[20px] text-[#F87171]" />
              </div>
              <div className="basis-0 grow h-full min-h-px min-w-px relative shrink-0">
                <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex gap-[10px] items-center relative size-full">
                  <p
                    className="font-normal leading-[24px] not-italic relative shrink-0 text-[14px] text-nowrap text-white tracking-[-0.3125px] whitespace-pre"
                    data-testid="reboot-live-player-select-error"
                  >
                    {error}
                  </p>
                  {allowRetry && onRetry && (
                    <div className="basis-0 content-stretch flex gap-[10px] grow items-center justify-end min-h-px min-w-px relative shrink-0">
                      <button
                        data-testid="reboot-live-player-select-retry-button"
                        onClick={onRetry}
                        className="cursor-pointer"
                      >
                        <p className="font-normal leading-[24px] not-italic relative shrink-0 text-[#fb6a07] text-[14px] text-nowrap tracking-[-0.3125px] whitespace-pre">
                          Retry
                        </p>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <p
        data-testid="reboot-live-player-select-prompt"
        className="font-medium leading-[23px] not-italic relative shrink-0 text-[18px] text-center text-white"
      >
        Who are we analyzing today?
      </p>
      <div
        ref={containerRef}
        data-testid="reboot-live-player-select-container"
        className="bg-[#1d1d1f] relative rounded-[12px] shrink-0 w-[600px] overflow-visible"
      >
        <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex flex-col h-full items-start pb-[32px] pt-[32px] px-[32px] relative w-[600px]">
          <div className="w-full">
            <DarkThemeSingleSelect
              value={selectedPlayer}
              onChange={(value) => {
                onPlayerChange(value || "");
              }}
              options={playerOptions}
              placeholder="Select a player..."
              withSearch={true}
              inputValue={playerSearchInput}
              onInputChange={setPlayerSearchInput}
              styles={rebootPlayerSelectStyles}
              className="w-full"
              classNamePrefix="reboot-live-player-select"
              isDisabled={isDisabled}
              onMenuOpen={() => {
                containerRef.current?.scrollIntoView({
                  block: "start",
                  behavior: "smooth",
                });
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
